import { requireDashboardOrigin } from "@playbrain/auth/server/dashboardOriginGuard"
import { requireDashboardAuth } from "@playbrain/auth/server/requireDashboardAuth"
import { logEvent, shortLinksDeployed } from "@playbrain/config"
import * as z from "zod"

/**
 * The dashboard's side of the short-links panel: "does this record already have
 * a short link" and "make one for it".
 *
 * Authorization is the same SQL-backed identity the OIDC provider resolves at
 * sign-in — the session cookie only locates the row, and everything that
 * decides access (active account, short-links read, which domains) is read
 * fresh. Creation additionally requires the target domain to be one that
 * identity holds; Kutt re-checks the grant regardless, so a mistake here is a
 * worse error message rather than a hole.
 */

const searchInputSchema = z.object({
  target: z.string().url().max(2048).optional(),
  address: z.string().min(1).max(64).optional(),
})

const createInputSchema = z.object({
  target: z.string().url().max(2048),
  address: z.string().min(1).max(64),
  domain: z.string().min(1).max(253),
})

type ShortLinksIdentity = {
  email: string
  domains: string[]
}

async function resolveIdentity(request: Request): Promise<ShortLinksIdentity | Response> {
  const auth = await requireDashboardAuth(request, [])
  if ("response" in auth) return auth.response

  const { currentShortLinksStage } = await import("@api/features/short-links-sso/shortLinksOidcHandlers")
  const { resolveShortLinksOidcIdentity } = await import("@api-users/features/short-links/shortLinksAccess")
  const identity = await resolveShortLinksOidcIdentity(
    auth.user as { sub?: unknown; email?: unknown },
    currentShortLinksStage()
  )

  if (!identity) {
    logEvent("warn", "short_links.api_denied", { reason: "no_short_links_access" })
    return Response.json(
      { error: "You don't have permission to use short links. Please contact an administrator." },
      { status: 403 }
    )
  }
  return { email: identity.email, domains: identity.domains }
}

/** Existing short links for a record, plus the domains this user may create on. */
export async function handleShortLinkSearch(request: Request): Promise<Response> {
  const originError = requireDashboardOrigin(request)
  if (originError) return originError

  const identity = await resolveIdentity(request)
  if (identity instanceof Response) return identity

  // A stage with no short-links service has a real answer, not an error: no
  // record on it has a short link, and no domain accepts one. Erroring here
  // left the row's copy button permanently "unresolved" — enabled, promising a
  // short link, and copying nothing — on exactly the stages where it should
  // simply be dark.
  if (!shortLinksDeployed()) {
    return Response.json({ links: [], domains: [] })
  }

  try {
    const input = searchInputSchema.parse(await request.json())
    const { searchManagedShortLinks } = await import("@api/features/short-links/shortLinksManagedApi")
    const result = await searchManagedShortLinks({
      email: identity.email,
      address: input.address ?? null,
      target: input.target ?? null,
    })
    // The domains the dashboard computed are the ones the picker may offer;
    // Kutt reports what it granted, and the two agree by construction.
    return Response.json({ links: result.links, domains: identity.domains })
  } catch (error) {
    return shortLinksErrorResponse(error, "search", "Could not look up short links.")
  }
}

/** Creates a short link for a record, owned by the person who asked for it. */
export async function handleShortLinkCreate(request: Request): Promise<Response> {
  const originError = requireDashboardOrigin(request)
  if (originError) return originError

  const identity = await resolveIdentity(request)
  if (identity instanceof Response) return identity

  try {
    const input = createInputSchema.parse(await request.json())
    if (!identity.domains.includes(input.domain)) {
      logEvent("warn", "short_links.api_denied", { reason: "domain_not_granted" })
      return Response.json(
        { error: "You don't have permission to create links on that domain." },
        { status: 403 }
      )
    }

    const { createManagedShortLink } = await import("@api/features/short-links/shortLinksManagedApi")
    const link = await createManagedShortLink({ email: identity.email, ...input })
    return Response.json({ link }, { status: 201 })
  } catch (error) {
    return shortLinksErrorResponse(error, "create", "Could not create the short link.")
  }
}

/**
 * Kutt's refusals are the ones a person can act on — a taken address, a domain
 * they do not hold — so those are relayed with their own status. Everything
 * else collapses to a generic message, because the detail belongs in the log
 * rather than in the browser.
 */
function shortLinksErrorResponse(error: unknown, operation: string, fallback: string): Response {
  logEvent("error", `short_links.api_${operation}_failed`, {
    reason: error instanceof Error ? error.message : String(error),
  })

  if (error instanceof z.ZodError) {
    return Response.json({ error: fallback }, { status: 400 })
  }
  if (isShortLinksApiError(error) && error.status >= 400 && error.status < 500) {
    return Response.json({ error: error.message }, { status: error.status })
  }
  return Response.json({ error: fallback }, { status: 502 })
}

function isShortLinksApiError(error: unknown): error is { message: string; status: number } {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: unknown }).code === "SHORT_LINKS_API_FAILED" &&
    typeof (error as { status?: unknown }).status === "number"
  )
}
